import { SocialUniverseChannel } from '@digital-twins/shared-types'
import type { TFunction } from 'i18next'

import { formatSocialCredits, formatSocialRelativeTime } from './socialCredits'

/**
 * What an "Update now" control says and whether it is blocked (design S4,
 * S5, S6): paused for credits ("More credits needed."), cooling down
 * ("Available again in 40 minutes."), else its maximum cost while charging
 * is on. The server owns every fact (cooldown end, ceiling, pause reason);
 * the server's 429 / 402 stay the backstop.
 */
export interface SocialUpdateNowState {
  /** True while cooling down or paused for credits: the control is disabled. */
  isBlocked: boolean
  /** Tooltip text; null shows no tooltip. */
  reason: string | null
  /** Trailing text inside a menu item (Radix disabled items host no tooltip); null when none. */
  shortReason: string | null
}

export interface SocialUpdateNowInput {
  t: TFunction<'app'>
  lng: string | null | undefined
  now: number
  /** The Topic or channel is paused for credits. */
  isCreditPaused: boolean
  /** When "Update now" is allowed again; null or past when allowed now. */
  availableAt: string | null | undefined
  /** Whole credits one update can hold; absent hides the cost line. */
  maxCreditsPerUpdate: number | undefined
  /** `update` (channel, card: "...You pay only for posts added.") or `updateAll` (Activity footer). */
  costKey?: 'update' | 'updateAll'
}

export function socialUpdateNowState(input: SocialUpdateNowInput): SocialUpdateNowState {
  const { t, lng, now, availableAt, maxCreditsPerUpdate, costKey = 'update' } = input
  if (input.isCreditPaused) {
    return {
      isBlocked: true,
      reason: t('socialListening.credits.update.needsCredits'),
      shortReason: t('socialListening.credits.card.paused'),
    }
  }

  const availableMs = availableAt ? Date.parse(availableAt) : Number.NaN
  if (Number.isFinite(availableMs) && availableMs > now) {
    const when = formatSocialRelativeTime(availableMs, lng, now)
    return {
      isBlocked: true,
      reason: t('socialListening.credits.update.cooldown', { when }),
      shortReason: when,
    }
  }

  if (typeof maxCreditsPerUpdate === 'number' && maxCreditsPerUpdate > 0) {
    return {
      isBlocked: false,
      reason: t(`socialListening.credits.${costKey}.cost`, {
        count: maxCreditsPerUpdate,
        value: formatSocialCredits(maxCreditsPerUpdate, lng, { ceiling: true }),
      }),
      shortReason: null,
    }
  }

  return { isBlocked: false, reason: null, shortReason: null }
}

/** A channel paused for credits (status stays `paused`, the reason says why). */
export function isChannelCreditPaused(
  channel: Pick<SocialUniverseChannel, 'status' | 'pauseReason'>,
): boolean {
  return channel.status === 'paused' && channel.pauseReason === 'out_of_credits'
}

export type SocialChannelStatusKey =
  | 'socialListening.status.pausedOutOfCredits'
  | 'socialListening.status.pausedByOps'
  | `socialListening.status.${SocialUniverseChannel['status']}`

/** The i18n key of a channel's status badge: the pause reason picks the paused wording (S4). */
export function channelStatusKey(
  channel: Pick<SocialUniverseChannel, 'status' | 'pauseReason'>,
): SocialChannelStatusKey {
  if (channel.status === 'paused' && channel.pauseReason === 'out_of_credits') {
    return 'socialListening.status.pausedOutOfCredits'
  }
  if (channel.status === 'paused' && channel.pauseReason === 'ops') {
    return 'socialListening.status.pausedByOps'
  }
  return `socialListening.status.${channel.status}`
}
